import InputAdornment from '@mui/material/InputAdornment';
import { Grid } from '@mui/material';

import { TextField, StatesSingleAutocomplete } from '@occc-app/design-system';
import { Typography } from '@occc/ui-design-system';
import { parseNumber } from '@occc/ui-forms';

import { getInputProps } from './retailer-network-configuration.utils';

const nameInputProps = getInputProps('name');
const displayNameInputProps = getInputProps('displayName');
const adminNameInputProps = getInputProps('adminName');
const streetInputProps = getInputProps('street');
const cityInputProps = getInputProps('city');
const stateInputProps = getInputProps('state');
const zipCodeInputProps = getInputProps('zipCode');
const phoneNumberInputProps = getInputProps('phoneNumber');
const emailAddressInputProps = getInputProps('emailAddress');
const membersPerStoreInputProps = getInputProps('membersPerStore');
const transactionsPerStoreInputProps = getInputProps('transactionsPerStore');

// TODO: this styles should be applied globally during redesign iteration
const inputStyleOverrides = {
  // White background for disabled inputs
  '& input': {
    backgroundColor: 'inherit',
  },
};

type NetworkConfigurationFormContentProps = {
  disabled: boolean;
  isEditingExistingNetwork?: boolean;
  expectedNewMembersGoalOutcome?: string;
  expectedTransactionsGoalOutcome?: string;
};

export const NetworkDetailsFormContent = ({ disabled }: NetworkConfigurationFormContentProps) => {
  return (
    <Grid container spacing={3}>
      <Grid item md={4}>
        <TextField
          name='name'
          label='Network Name'
          inputProps={nameInputProps}
          disabled={disabled}
          sx={inputStyleOverrides}
          required
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          name='displayName'
          label='Network Display Name'
          inputProps={displayNameInputProps}
          disabled={disabled}
          sx={inputStyleOverrides}
          required
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          name='adminName'
          label='Network Contact'
          inputProps={adminNameInputProps}
          disabled={disabled}
          sx={inputStyleOverrides}
          required
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          name='phoneNumber'
          label='Network Phone Number'
          inputProps={phoneNumberInputProps}
          disabled={disabled}
          sx={inputStyleOverrides}
          required
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          name='emailAddress'
          label='Network Email'
          inputProps={emailAddressInputProps}
          disabled={disabled}
          sx={inputStyleOverrides}
          required
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          name='address.street1'
          label='Network Street'
          inputProps={streetInputProps}
          disabled={disabled}
          sx={inputStyleOverrides}
          required
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          name='address.city'
          label='Network City'
          inputProps={cityInputProps}
          disabled={disabled}
          sx={inputStyleOverrides}
          required
        />
      </Grid>
      <Grid item md={4}>
        <StatesSingleAutocomplete
          name='address.state'
          label='Network State'
          disabled={disabled}
          inputProps={{ required: true, ...stateInputProps, margin: 'none' }}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          name='address.zipCode'
          label='Network Zip Code'
          inputProps={zipCodeInputProps}
          disabled={disabled}
          sx={inputStyleOverrides}
          required
        />
      </Grid>
    </Grid>
  );
};

const PerMonthInputAdornment = () => (
  <InputAdornment position='end' sx={{ ml: 1 }}>
    per month
  </InputAdornment>
);

export const NetworkMonthlyGoalsFormContent = ({
  disabled,
  isEditingExistingNetwork,
  expectedNewMembersGoalOutcome = '0',
  expectedTransactionsGoalOutcome = '0',
}: NetworkConfigurationFormContentProps) => {
  return (
    <>
      <Grid container spacing={3} alignItems='center'>
        <Grid item md={4}>
          <Typography variant='body1' color={disabled ? 'textSecondary' : undefined}>
            New members per Store Goal
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            Encourage new member enrollment
          </Typography>
        </Grid>
        <Grid item md={4}>
          <TextField
            name='monthlyPerformanceGoal.membersPerStore'
            inputProps={membersPerStoreInputProps}
            InputProps={{
              endAdornment:
                disabled || !isEditingExistingNetwork ? null : <PerMonthInputAdornment />,
            }}
            label={!isEditingExistingNetwork ? 'New Members' : null}
            sx={inputStyleOverrides}
            disabled={disabled}
            required
            margin='normal'
            fieldProps={{
              parse: parseNumber,
            }}
          />
        </Grid>
        <Grid item md={4}>
          <Typography variant='body1' color={disabled ? 'textSecondary' : undefined}>
            {isEditingExistingNetwork ? `${expectedNewMembersGoalOutcome} ` : ''}
            new members per month
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems='center'>
        <Grid item md={4}>
          <Typography variant='body1' color={disabled ? 'textSecondary' : undefined}>
            Loyalty Transactions per Store Goal
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            Increase total number of loyalty transactions
          </Typography>
        </Grid>
        <Grid item md={4}>
          <TextField
            name='monthlyPerformanceGoal.transactionsPerStore'
            inputProps={transactionsPerStoreInputProps}
            InputProps={{
              endAdornment:
                disabled || !isEditingExistingNetwork ? null : <PerMonthInputAdornment />,
            }}
            label={!isEditingExistingNetwork ? 'Transactions' : null}
            sx={inputStyleOverrides}
            disabled={disabled}
            required
            margin='normal'
            fieldProps={{
              parse: parseNumber,
            }}
          />
        </Grid>
        <Grid item md={4}>
          <Typography variant='body1' color={disabled ? 'textSecondary' : undefined}>
            {isEditingExistingNetwork ? `${expectedTransactionsGoalOutcome} ` : ''}
            new loyalty transactions per month
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};
